import compose from 'lodash/fp/compose'
import { CubeDomainInterface } from 'modules/Cube/domain/cube.domain'
import { QuoteBlock } from '@sequencehq/quote-content'
import { blockAdapters } from 'modules/Cube/view/layouts/quote/QuoteEditorContent/ContentEditor/adapters/blocks'
import { blocknoteSchema } from 'modules/Cube/view/layouts/quote/QuoteEditorContent/ContentEditor/blocknoteSchema'
import { PresentationV3 } from 'Cube/domain/cube.domain.types'

const applyNumberingToNestedLists = (
  blocks: Array<typeof blocknoteSchema.Block>
): Array<typeof blocknoteSchema.Block> => {
  return blocks.map((block, idx) => {
    if (!['numberedListItem', 'bulletListItem'].includes(block.type)) {
      return block
    }

    if (block.type === 'numberedListItem') {
      return {
        ...block,
        children: applyNumberingToNestedLists(block.children),
        props: {
          ...block.props,
          start: block.props?.start ?? idx + 1
        }
      }
    }

    return {
      ...block,
      children: applyNumberingToNestedLists(block.children)
    }
  })
}

/**
 * Blocknote has an internal mechanism for numbering the items within a list.
 * This means the representation of numbered lists is also reasonably simple,
 * as each item is fully independent. However, we're not in blocknote right
 * now, so we'll need to manually add in the numbering props to each numbered list
 * item so that they can be rendered neatly!
 *
 * @param blockNote
 * @returns
 */
export const getNumberForBlock = ({
  previousBlock,
  block
}: {
  previousBlock: typeof blocknoteSchema.Block | undefined
  block: Extract<typeof blocknoteSchema.Block, { type: 'numberedListItem' }>
}) => {
  if (!previousBlock || previousBlock.type !== 'numberedListItem') {
    return block.props.start ?? 1
  }

  /**
   * There's an interesting scenario here where adding a new numbered list
   * item above an existing one, with a new number, does change the rest
   * of the list. Therefore, to mimic the behaviour, we want the previous
   * block start value to take priority over the current block's start value.
   */
  return (previousBlock.props.start ?? block.props.start ?? 0) + 1
}

export const addNumbersToNumberedLists = (
  blockNote: (typeof blocknoteSchema.BlockNoteEditor)['document']
): (typeof blocknoteSchema.BlockNoteEditor)['document'] => {
  return blockNote.reduce(
    (acc, block) => {
      /**
       * Only modify numbered list items
       */
      if (!['numberedListItem', 'bulletListItem'].includes(block.type)) {
        return [...acc, block]
      }

      if (block.type === 'numberedListItem') {
        const previousBlock = acc.at(-1)

        return [
          ...acc,
          {
            ...block,
            children: applyNumberingToNestedLists(block.children),
            props: {
              ...block.props,
              start: getNumberForBlock({ previousBlock, block })
            }
          }
        ]
      }

      return [
        ...acc,
        {
          ...block,
          children: applyNumberingToNestedLists(block.children)
        }
      ]
    },
    [] as (typeof blocknoteSchema.BlockNoteEditor)['document']
  )
}

export const presentationToBlockNoteAdapter = {
  out:
    (ctx: { cubeQueries: CubeDomainInterface['queries'] }) =>
    (blockNote: (typeof blocknoteSchema.BlockNoteEditor)['document']) => {
      const preparedBlocknote = compose(addNumbersToNumberedLists)(
        blockNote
      ) as (typeof blocknoteSchema.BlockNoteEditor)['document']

      const transformed = preparedBlocknote
        .map(block => {
          const adapter = blockAdapters.out[block.type]
          if (!adapter) {
            return null
          }
          return adapter(ctx)(block) as QuoteBlock
        })
        .filter(Boolean) as QuoteBlock[]

      return transformed
    },
  in: (
    presentation: PresentationV3
  ): Array<typeof blocknoteSchema.PartialBlock> => {
    const transformed = presentation.blocks.flatMap(block => {
      const adapter = blockAdapters.in[block.blockType]
      if (!adapter) {
        return []
      }
      return adapter(block)
    })

    if (transformed.length === 0) {
      return [
        {
          type: 'paragraph'
        }
      ]
    }

    return transformed
  }
}
